import React, { useState } from 'react';
import moment from 'moment-timezone';
import { CalendarDays, ChevronLeft, ChevronRight, Lock, Trophy } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { priorPeriodToDate, rangeLabel } from '../../../../lib/dateRange';
import { DateRangePicker } from '../../onboardingSupport/DateRangeCalendar';
import { Avatar, CARD_SHADOW, Card, SURFACE } from './premium';

// Shared pieces for the Sales Team and Onboarding Team tabs (Nic 2026-10-01,
// restyled to the /billing/envoy look 2026-10-25).

const CT = 'America/Chicago';

// ---------------------------------------------------------------- Period
//
// One period key for every Lead Metrics tab (Nic 2026-10-25): a year
// ('2026'), a quarter ('2026-Q4'), a month ('2026-10'), or a custom range
// ('2026-10-03~2026-10-09', both days included). Central time throughout.

const CUSTOM_RE = /^(\d{4}-\d{2}-\d{2})~(\d{4}-\d{2}-\d{2})$/;

export const kindOf = (key) => {
  if (CUSTOM_RE.test(key || '')) return 'custom';
  if (/^\d{4}$/.test(key || '')) return 'year';
  if (/Q/.test(key || '')) return 'quarter';
  return 'month';
};

export const customKey = (start, end) => `${start}~${end}`;

export const currentPeriod = (kind) => {
  const now = moment.tz(CT);
  if (kind === 'year') return String(now.year());
  if (kind === 'month') return now.format('YYYY-MM');
  return `${now.year()}-Q${now.quarter()}`;
};

// First day of the period, and its last day (both CT, start of day).
const toMoment = (key) => {
  const c = CUSTOM_RE.exec(key || '');
  if (c) return moment.tz(c[1], 'YYYY-MM-DD', CT);
  if (/^\d{4}$/.test(key || '')) return moment.tz(`${key}-01-01`, 'YYYY-MM-DD', CT);
  const q = /^(\d{4})-Q([1-4])$/.exec(key || '');
  if (q) return moment.tz(`${q[1]}-01-01`, 'YYYY-MM-DD', CT).add((Number(q[2]) - 1) * 3, 'months');
  return moment.tz(`${key}-01`, 'YYYY-MM-DD', CT);
};
const lastDayOf = (key) => {
  const c = CUSTOM_RE.exec(key || '');
  if (c) return moment.tz(c[2], 'YYYY-MM-DD', CT);
  return toMoment(key).endOf(kindOf(key)).startOf('day');
};

const keyFor = (kind, m) =>
  kind === 'year' ? String(m.year()) : kind === 'quarter' ? `${m.year()}-Q${m.quarter()}` : m.format('YYYY-MM');

const step = (key, dir) => {
  const kind = kindOf(key);
  return keyFor(kind, toMoment(key).add(dir, kind));
};

export const periodLabel = (key) => {
  const kind = kindOf(key);
  const m = toMoment(key);
  if (kind === 'custom') {
    const c = CUSTOM_RE.exec(key);
    return rangeLabel(c[1], c[2]);
  }
  if (kind === 'year') return String(m.year());
  return kind === 'quarter' ? `Q${m.quarter()} ${m.year()}` : m.format('MMMM YYYY');
};

// The dates a period covers, for the Funnel (CT, 'YYYY-MM-DD'): from its
// first day to its last, or to today while it runs, compared with the period
// before to the same point (prior period-to-date). A custom range sends no
// comparison: the server compares it with the equal stretch before it.
export const periodRange = (key) => {
  const kind = kindOf(key);
  const s = toMoment(key).format('YYYY-MM-DD');
  if (kind === 'custom') return { start: s, end: lastDayOf(key).format('YYYY-MM-DD') };
  const today = moment.tz(CT).startOf('day');
  const last = lastDayOf(key);
  const e = moment.min(last, today).format('YYYY-MM-DD');
  // A finished period compares with the whole period before it (counting
  // days would drop Dec 31 when last year was a leap year); a running one
  // with the period before to the same point.
  if (e === last.format('YYYY-MM-DD')) {
    const prev = step(key, -1);
    return {
      start: s,
      end: e,
      prevStart: toMoment(prev).format('YYYY-MM-DD'),
      prevEnd: lastDayOf(prev).format('YYYY-MM-DD'),
    };
  }
  return { start: s, end: e, ...priorPeriodToDate(kind)(s, e) };
};

const KINDS = ['year', 'quarter', 'month'];

// Year | Quarter | Month | Custom, then the arrows (Envoy's header
// controls). Switching kind keeps you on the same stretch of time: the
// year, quarter or month holding the period's last day (or today, while it
// runs). Custom opens the Envoy page's two-month calendar; a custom range
// unlights the others and hides the arrows. Never steps past now.
export const PeriodBar = ({ value, onChange }) => {
  const [picking, setPicking] = useState(false);
  const kind = kindOf(value);
  const isCustom = kind === 'custom';
  const atNow = !isCustom && value === currentPeriod(kind);
  const switchKind = (k) => {
    if (k === kind) return;
    const anchor = moment.min(lastDayOf(value), moment.tz(CT).startOf('day'));
    onChange(keyFor(k, anchor));
  };
  const pill = (active) =>
    cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1 text-xs font-semibold capitalize outline-none transition-[color] focus-visible:ring-2 focus-visible:ring-sky-400/60',
      active
        ? 'bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900'
        : 'text-slate-500 hover:text-slate-800 dark:hover:text-white',
    );
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <div className={cn('flex items-center gap-0.5 rounded-xl p-1', SURFACE, CARD_SHADOW)}>
        {KINDS.map((k) => (
          <button key={k} type="button" onClick={() => switchKind(k)} className={pill(kind === k)}>
            {k}
          </button>
        ))}
        <DateRangePicker
          open={picking}
          onOpenChange={setPicking}
          value={isCustom ? periodRange(value) : null}
          onCommit={(r) => onChange(customKey(r.start, r.end))}
        >
          <button type="button" className={pill(isCustom)}>
            <CalendarDays size={13} />
            {isCustom ? periodLabel(value) : 'Custom'}
          </button>
        </DateRangePicker>
      </div>
      {!isCustom ? (
        <div className={cn('flex items-center rounded-xl', SURFACE, CARD_SHADOW)}>
          <button
            type="button"
            onClick={() => onChange(step(value, -1))}
            aria-label="Previous"
            className="flex h-8 w-8 items-center justify-center rounded-l-xl text-slate-400 outline-none hover:text-slate-800 focus:outline-none dark:hover:text-white"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[7.5rem] text-center text-xs font-semibold tabular-nums text-slate-800 dark:text-white">
            {periodLabel(value)}
          </span>
          <button
            type="button"
            onClick={() => onChange(step(value, 1))}
            disabled={atNow}
            aria-label="Next"
            className="flex h-8 w-8 items-center justify-center rounded-r-xl text-slate-400 outline-none hover:text-slate-800 focus:outline-none disabled:opacity-30 dark:hover:text-white"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------- Format

// Under the hero title: tracking starts at launch (nothing before it counts).
export const periodNote = (p) => {
  if (!p) return null;
  const since = p.trackingSince ? moment.tz(p.trackingSince, CT).format('MMM D, YYYY') : null;
  if (p.beforeTracking) return `Tracking starts ${since}. Nothing before launch counts.`;
  if (since && p.running) return `So far, tracked since ${since}`;
  return p.running ? 'So far this period' : 'The whole period';
};

// "vs last month" (running periods compare to the same point last time).
const BEFORE = {
  year: 'last year',
  quarter: 'last quarter',
  month: 'last month',
  custom: 'the same stretch before',
};
export const vsText = (p) => (p?.kind === 'custom' ? 'vs before' : `vs ${BEFORE[p?.kind] || 'last quarter'}`);
export const vsTitle = (p) =>
  `Compared with ${BEFORE[p?.kind] || 'last quarter'}${p?.running && p?.kind !== 'custom' ? ' at this same point' : ''}`;

export const fmtPct = (v) => (v == null ? '-' : `${Math.round(v)}%`);
export const fmtDays = (v) => (v == null ? '-' : v < 1 ? '< 1 day' : `${Math.round(v * 10) / 10} days`);
export const fmtMoney = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtK = (v) => (v >= 1000 ? `$${Math.round(v / 100) / 10}k` : fmtMoney(v));

// 95 -> "1h 35m", 40 -> "40m", 0 -> "0m".
export const fmtMinutes = (m) => {
  const n = Math.round(Number(m) || 0);
  const h = Math.floor(n / 60);
  return h ? `${h}h ${String(n % 60).padStart(2, '0')}m` : `${n}m`;
};

// ---------------------------------------------------------------- Bonus

// Quarterly bonus progress per person: one shared scale (each tier's count
// and payout) over a track per person. Admins and the Onboarding Manager
// only (the whole tab is).
// The Source pick as words, for a scoreboard subtitle ('' = all sources).
const SOURCE_NAMES = {
  envoy: 'Envoy',
  'referral-program': 'Referral Program',
  partners: 'Partners',
  events: 'Events',
  website: 'Website',
  manual: 'Manual / Other',
  direct: 'Direct Signups',
};
export const sourceName = (filter, filterOptions) => {
  if (filter?.partnerId) {
    return (filterOptions?.events || []).find((e) => e.partnerId === filter.partnerId)?.name || 'One event';
  }
  return SOURCE_NAMES[filter?.source] || '';
};

// `allSources`: a Source filter is on elsewhere on the tab; bonuses are paid
// on every agreement or FOTC, so this card says it isn't filtered.
export const BonusLadder = ({ bonus, unit, colors = {}, allSources = false }) => {
  if (!bonus) return null;
  const { tiers } = bonus;
  const pending = tiers.some((t) => t.pending);
  // A milestone track (Nic 2026-11-02): every tier gets an equal segment,
  // after a short run-up for "0 to the first tier", so the labels always
  // have the same room and never collide (on the linear scale every tier
  // sat in the top half and five labels fought over it). Progress inside
  // a segment is proportional, so the bar still moves every day. The
  // run-up's width is the --runup variable (narrower on a phone).
  const segments = Math.max(1, tiers.length - 1);
  const columns = `var(--runup) repeat(${segments}, minmax(0, 1fr))`;
  // Where a tier boundary sits: the run-up's end, then each segment's end.
  const edge = (k) => (k <= 0 ? 'var(--runup)' : `calc(var(--runup) + (100% - var(--runup)) * ${k / segments})`);
  const fillOf = (count) => {
    const first = tiers[0].at;
    if (count < first) return `calc(var(--runup) * ${first ? Math.max(0, count / first) : 0})`;
    let k = 0;
    for (let n = 0; n < tiers.length; n += 1) if (count >= tiers[n].at) k = n;
    if (k >= tiers.length - 1) return '100%';
    const frac = (count - tiers[k].at) / (tiers[k + 1].at - tiers[k].at);
    return `calc(var(--runup) + (100% - var(--runup)) * ${(k + frac) / segments})`;
  };
  return (
    <Card
      eyebrow={`${bonus.quarter} · quarter to date${allSources ? ' · all sources' : ''}`}
      title="Bonus progress"
      info={`Counted on ${unit} for the whole quarter, whatever period is picked above. Each tier is an equal step on the track; the first step is the run-up to tier one.`}
      right={
        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 dark:bg-white/10 dark:text-slate-300">
          <Lock size={11} /> Admins and the Onboarding Manager only
        </span>
      }
    >
      <div className="grid grid-cols-[minmax(0,8rem)_1fr] items-center gap-x-4 gap-y-4 [--runup:1.25rem] sm:grid-cols-[minmax(0,11rem)_1fr] sm:gap-x-5 sm:[--runup:3.5rem]">
        {/* The scale: each tier's count over its payout, right-aligned on
            its boundary. */}
        <span />
        <div className="grid items-end" style={{ gridTemplateColumns: columns }}>
          {tiers.map((t, i) => (
            <span
              key={t.at}
              className={cn(
                'flex flex-col items-end text-[10px] leading-tight',
                // Tier one labels the run-up; the last tier hugs the end.
                i === 0 ? 'col-start-1' : '',
              )}
              style={i === 0 ? undefined : { gridColumn: i + 1 }}
            >
              <b className="font-semibold tabular-nums text-slate-700 dark:text-slate-200">{t.at}</b>
              <span className="whitespace-nowrap tabular-nums text-emerald-600 dark:text-emerald-400">
                {fmtK(t.bonus)}
                {t.pending ? '*' : ''}
              </span>
            </span>
          ))}
        </div>
        {bonus.people.map((p) => (
          <React.Fragment key={p.userId}>
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar name={p.name} color={colors[p.userId]} />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-800 dark:text-white">{p.name}</div>
                <div className="text-[11px] text-slate-400">
                  <b className="font-semibold tabular-nums text-slate-700 dark:text-slate-200">{p.count}</b> {unit}
                </div>
              </div>
            </div>
            <div>
              <div className="relative h-2.5 rounded-full bg-slate-100 dark:bg-white/10">
                <div
                  className={cn(
                    'h-full rounded-full bg-gradient-to-r transition-[width] duration-700',
                    p.reached ? 'from-emerald-500 to-teal-400' : 'from-indigo-500 to-violet-400',
                  )}
                  style={{ width: fillOf(p.count) }}
                />
                {tiers.map((t, i) => (
                  <span
                    key={t.at}
                    aria-hidden
                    className={cn(
                      'absolute -top-[3px] h-4 w-0.5 rounded-sm',
                      p.count >= t.at ? 'bg-white/80' : 'bg-slate-300 dark:bg-white/20',
                    )}
                    style={{ left: edge(i), transform: 'translateX(-50%)' }}
                  />
                ))}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                {p.reached ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-px font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                    <Trophy size={11} /> {fmtMoney(p.reached.bonus)}
                    {p.reached.pending ? '*' : ''} reached
                  </span>
                ) : null}
                {p.next ? (
                  <span>
                    {p.next.needs} more to {fmtMoney(p.next.bonus)}
                    {p.next.pending ? '*' : ''}
                  </span>
                ) : (
                  <span>Top tier</span>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
      {pending ? (
        <p className="mb-0 mt-4 text-[11px] text-slate-400">* Pending confirmation from leadership.</p>
      ) : null}
    </Card>
  );
};
